import { Fragment, useEffect } from 'react';
import { NextSeo, BreadcrumbJsonLd } from 'next-seo';
import { useAmp } from 'next/amp';
import { LazyLoad } from 'react-observer-api';
import typeOf from 'typeof';
import axios from 'axios';
import { declOfNum, capitalizeFirstLetter, toMoney } from '../../helpers';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import { useAppProvider } from '../../context/app';
import ObjectInformationContent from '../../components/object/ObjectInformationContent';
import ObjectInformationOwner from '../../components/object/ObjectInformationOwner';
import ObjectSimilar from '../../components/object/ObjectSimilar';
import ObjectContactsMobile from '../../components/object/ObjectContactsMobile';
import language from '../../data/language.json';
import styles from '../../styles/modules/object/Object.module.scss';

export const config = { amp: 'hybrid' };

const categoryToSEO = (c) => {
  switch (c) {
    default:
    case 'квартира':
      return { text: 'Квартиры', link: 'flats' };

    case 'комната':
      return { text: 'Комнаты', link: 'rooms' };

    case 'дом':
      return { text: 'Дома', link: 'houses' };

    case 'участок':
      return { text: 'Участки', link: 'sites' };

    case 'коммерция':
      return { text: 'Коммерция', link: 'commercial' };
  }
};
const flatsToSEO = (f) => {
  switch (f) {
    case 'студия':
      return { text: 'Студии', link: 'studio' };

    default:
    case 1:
      return { text: '1-комнатные', link: '1k' };

    case 2:
      return { text: '2-комнатные', link: '2k' };

    case 3:
      return { text: '3-комнатные', link: '3k' };

    case 4:
      return { text: '4-комнатные', link: '4k' };
  }
};

const ObjectPage = ({ id, data }) => {
  const isAmp = useAmp();
  const [appState, setAppState] = useAppProvider();

  const getAddress = (city = false, street = false, house = false, declension = false) => {
    const cityCase = (ct) =>
      ({
        'Ростов-на-Дону': 'Ростове-на-Дону',
        Новочеркаск: 'Новочеркаске',
        Батайск: 'Батайске',
        Аксай: 'Аксае',
        Азов: 'Азове',
        Таганрог: 'Таганроге'
      })[ct] || ct;

    const arr = [];

    if (city && data.location.region && !data.location['locality-name'])
      arr.push(data.location.region);
    if (city && data.location['locality-name'])
      arr.push(
        declension ? cityCase(data.location['locality-name']) : data.location['locality-name']
      );
    if (street && data.location.street) arr.push(data.location.street);
    if (house && data.location.house) arr.push(data.location.house);

    return arr.join(', ');
  };
  const htmlTitleGenerator = () => {
    const commercialTypeCase = (tp) =>
      ({
        автосервис: 'автосервиса',
        гостиница: 'гостиницы',
        'готовый бизнес': 'готового бизнеса',
        'коммерческая земля': 'коммерческой земли',
        общепит: 'общепита',
        'офисное помещение': 'офисного помещения',
        ПСН: 'псн',
        'производственное помещение': 'производственного помещения',
        склад: 'склада',
        'торговое помещение': 'торгового помещения'
      })[tp] || tp;

    const arr = [];
    arr.push(data.type === 'продажа' ? 'Продажа' : 'Аренда');

    switch (data.category) {
      default:
      case 'квартира':
        if (data.object.studio) arr.push('квартиры-студии');
        else if (data.object.rooms) arr.push(`${data.object.rooms}-комнатной квартиры`);
        else arr.push('квартиры');

        if (data.object.area.total)
          arr.push(`${data.object.area.total.value} ${data.object.area.total.unit}`);

        break;

      case 'комната':
        if (data.object['rooms-offered'] === 1) arr.push('комнаты');
        else arr.push(data.object['rooms-offered'], 'комнат');

        if (data.object.area['living-space'])
          arr.push(
            `${data.object.area['living-space'].value} ${data.object.area['living-space'].unit}`
          );

        arr.push(`в ${data.object.rooms}-комнатной квартире`);

        break;

      case 'дом':
        if (data.object['floors-total'])
          arr.push(`${data.object['floors-total']}-этажного`, 'дома');
        else arr.push('дома');

        if (data.object.area.total)
          arr.push(`${data.object.area.total.value} ${data.object.area.total.unit}`);

        break;

      case 'участок':
        arr.push(
          `участка ${data.object.area['lot-area'].value} ${data.object.area['lot-area'].unit}`
        );

        break;

      case 'коммерция':
        arr.push(`${commercialTypeCase(data.object['commercial-type'])}`);

        if (data.object.area.total)
          arr.push(`${data.object.area.total.value} ${data.object.area.total.unit}`);
        else if (data.object.area['lot-area'])
          arr.push(`${data.object.area['lot-area'].value} ${data.object.area['lot-area'].unit}`);

        break;
    }

    arr.push(
      `по адресу ${getAddress(false, true, true, false)} в ${getAddress(
        true,
        false,
        false,
        true
      )}, объявление ${id}`
    );

    return arr.join(' ');
  };
  const htmlDescriptionGenerator = () => {
    const arr = [];

    arr.push(data.type === 'продажа' ? 'Купить' : 'Аренда');

    switch (data.category) {
      default:
      case 'квартира':
        if (data.object.studio) arr.push('квартиру-студию');
        else {
          if (data.object.rooms === 1) arr.push('однокомнатную');
          else if (data.object.rooms === 2) arr.push('двухкомнатную');
          else if (data.object.rooms === 3) arr.push('трехкомнатную');
          else if (data.object.rooms === 4) arr.push('четырехкомнатную');
          else if (data.object.rooms === 5) arr.push('пятикомнатную');

          arr.push('квартиру');
        }

        arr.push(
          `по адресу ${getAddress(false, true, true)} в городе ${getAddress(
            true,
            false,
            false,
            true
          )} за ${data.price.value} рублей в ${data.building['floors-total']}-этажном доме`
        );

        break;

      case 'комната':
        if (data.object.rooms === 2) arr.push('две', 'комнаты');
        else if (data.object.rooms === 3) arr.push('три', 'комнаты');
        else if (data.object.rooms === 4) arr.push('четыре', 'комнаты');
        else if (data.object.rooms === 5) arr.push('пять', 'комнаты');
        else arr.push('комнату');

        if (data.object.rooms === 1) arr.push('в однокомнатной');
        else if (data.object.rooms === 2) arr.push('в двухкомнатной');
        else if (data.object.rooms === 3) arr.push('в трехкомнатной');
        else if (data.object.rooms === 4) arr.push('в четырехкомнатной');
        else if (data.object.rooms === 5) arr.push('в пятикомнатной');

        arr.push(
          `по адресу ${getAddress(false, true, true, false)} в городе ${getAddress(
            true,
            false,
            false,
            true
          )} за ${data.price.value} рублей в ${data.building['floors-total']}-этажном доме`
        );

        break;

      case 'дом':
        if (data.object['floors-total'] === 1) arr.push('одноэтажный');
        else if (data.object['floors-total'] === 2) arr.push('двухэтажный');
        else if (data.object['floors-total'] === 3) arr.push('трехэтажный');
        else if (data.object['floors-total'] === 4) arr.push('четырехэтажный');
        else if (data.object['floors-total'] === 5) arr.push('пятиэтажный');

        arr.push('дом');
        arr.push(
          `по адресу ${getAddress(false, true, true, false)} в городе ${getAddress(
            true,
            false,
            false,
            true
          )} за ${data.price.value} рублей`
        );

        break;

      case 'участок':
        arr.push(
          `участок ${data.object.area['lot-area'].value} ${declOfNum(
            data.object.area['lot-area'].value,
            ['сотка', 'сотки', 'соток']
          )} по адресу ${getAddress(false, true, true, false)} в городе ${getAddress(
            true,
            false,
            false,
            true
          )} за ${data.price.value} рублей`
        );

        break;

      case 'коммерция':
        arr.push(
          `${data.object['commercial-type']} по адресу ${getAddress(
            false,
            true,
            true,
            false
          )} в городе ${getAddress(true, false, false, true)} за ${data.price.value} рублей`
        );

        break;
    }

    arr.push(`, объявление ${id}`);

    return arr.join(' ');
  };
  const breadcrumbsGenerator = () => {
    const readyData = [
      {
        position: 1,
        name: 'Вторичка',
        item: 'https://landlord.ru/objects'
      },
      {
        position: 2,
        name: data.type === 'аренда' ? 'Аренда' : 'Продажа',
        item: `https://landlord.ru/objects/${data.type === 'аренда' ? 'rent' : 'buy'}`
      },
      {
        position: 3,
        name: categoryToSEO(data.category).text,
        item: `https://landlord.ru/objects/${data.type === 'аренда' ? 'rent' : 'buy'}/${
          categoryToSEO(data.category).link
        }`
      }
    ];

    if (data.category === 'квартира') {
      readyData.push({
        position: 4,
        name: flatsToSEO(data.object.studio || data.object.rooms).text,
        item: `https://landlord.ru/objects/${data.type === 'аренда' ? 'rent' : 'buy'}/flats/${
          flatsToSEO(data.object.studio ? 'студия' : data.object.rooms).link
        }`
      });
    }

    return readyData;
  };
  const similarLinkGenerator = () => {
    let defaultLink = `/objects/${data.type === 'аренда' ? 'rent' : 'buy'}/${
      categoryToSEO(data.category).link
    }`;

    if (data.category === 'квартира') {
      defaultLink = `/objects/${data.type === 'аренда' ? 'rent' : 'buy'}/flats/${
        flatsToSEO(data.object.studio ? 'студия' : data.object.rooms).link
      }`;
    }

    return defaultLink;
  };
  const getAMPTitle = () => {
    const arr = [];

    switch (data.category) {
      default:
      case 'квартира':
        if (data.object.studio) arr.push('Квартира-студия');
        else if (data.object.rooms) arr.push(`${data.object.rooms}-комнатная квартира`);
        else arr.push('Квартира');

        if (data.object.area.total)
          arr.push(`${data.object.area.total.value} ${data.object.area.total.unit}`);

        break;

      case 'комната':
        if (data.object['rooms-offered'] === 1) arr.push('Комната');
        else
          arr.push(
            `${data.object['rooms-offered']} ${declOfNum(data.object['rooms-offered'], [
              'комната',
              'комнаты',
              'комнат'
            ])}`
          );

        if (data.object.area['living-space'])
          arr.push(
            `${data.object.area['living-space'].value} ${data.object.area['living-space'].unit}`
          );

        break;

      case 'дом':
        if (data.object['floors-total']) arr.push(`${data.object['floors-total']}-этажный дом`);
        else arr.push('Дом');

        if (data.object.area.total)
          arr.push(`${data.object.area.total.value} ${data.object.area.total.unit}`);

        break;

      case 'участок':
        arr.push(
          `участка ${data.object.area['lot-area'].value} ${data.object.area['lot-area'].unit}`
        );

        break;

      case 'коммерция':
        arr.push(capitalizeFirstLetter(data.object['commercial-type']));

        if (data.object.area.total)
          arr.push(`${data.object.area.total.value} ${data.object.area.total.unit}`);
        else if (data.object.area['lot-area'])
          arr.push(`${data.object.area['lot-area'].value} ${data.object.area['lot-area'].unit}`);

        break;
    }

    return arr.join(', ');
  };

  useEffect(() => {
    if (!appState.isLoaded) return;

    setAppState({
      type: 'SET_APP',
      payload: {
        ...appState,
        history: [id, ...appState.history]
      }
    });
    localStorage.setItem('history', JSON.stringify([id, ...appState.history]));
  }, [appState.isLoaded]);

  if (isAmp) {
    return (
      <Fragment>
        <NextSeo
          title={htmlTitleGenerator()}
          description={htmlDescriptionGenerator()}
          openGraph={{
            url: `https://landlord.ru/object/${id}`,
            type: 'website',
            title: htmlTitleGenerator(),
            description: data.description.replace(/\n|\r|"/g, ' '),
            images: [
              {
                url: data.images[0].value
              }
            ],
            site_name: 'LANDLORD'
          }}
        />
        <style jsx global>
          {`
            /** MAIN */
            @font-face {
              font-family: 'Gilroy';
              src: url('/fonts/Gilroy/Gilroy-Regular.eot');
              src:
                local('Gilroy Regular'),
                local('Gilroy-Regular'),
                url('/fonts/Gilroy/Gilroy-Regular.eot?#iefix') format('embedded-opentype'),
                url('/fonts/Gilroy/Gilroy-Regular.woff2') format('woff2'),
                url('/fonts/Gilroy/Gilroy-Regular.woff') format('woff'),
                url('/fonts/Gilroy/Gilroy-Regular.ttf') format('truetype');
              font-weight: normal;
              font-style: normal;
            }
            @font-face {
              font-family: 'Gilroy';
              src: url('/fonts/Gilroy/Gilroy-Bold.eot');
              src:
                local('Gilroy Bold'),
                local('Gilroy-Bold'),
                url('/fonts/Gilroy/Gilroy-Bold.eot?#iefix') format('embedded-opentype'),
                url('/fonts/Gilroy/Gilroy-Bold.woff2') format('woff2'),
                url('/fonts/Gilroy/Gilroy-Bold.woff') format('woff'),
                url('/fonts/Gilroy/Gilroy-Bold.ttf') format('truetype');
              font-weight: bold;
              font-style: normal;
            }
            @font-face {
              font-family: 'Gilroy';
              src: url('/fonts/Gilroy/Gilroy-Semibold.eot');
              src:
                local('Gilroy Semibold'),
                local('Gilroy-Semibold'),
                url('/fonts/Gilroy/Gilroy-Semibold.eot?#iefix') format('embedded-opentype'),
                url('/fonts/Gilroy/Gilroy-Semibold.woff2') format('woff2'),
                url('/fonts/Gilroy/Gilroy-Semibold.woff') format('woff'),
                url('/fonts/Gilroy/Gilroy-Semibold.ttf') format('truetype');
              font-weight: 600;
              font-style: normal;
            }
            @font-face {
              font-family: 'Gilroy';
              src: url('/fonts/Gilroy/Gilroy-Medium.eot');
              src:
                local('Gilroy Medium'),
                local('Gilroy-Medium'),
                url('/fonts/Gilroy/Gilroy-Medium.eot?#iefix') format('embedded-opentype'),
                url('/fonts/Gilroy/Gilroy-Medium.woff2') format('woff2'),
                url('/fonts/Gilroy/Gilroy-Medium.woff') format('woff'),
                url('/fonts/Gilroy/Gilroy-Medium.ttf') format('truetype');
              font-weight: 500;
              font-style: normal;
            }

            h1,
            h2,
            h3,
            h4,
            h5,
            h6 {
              font-size: 25px;
              font-family: 'Gilroy', sans-serif;
              margin: 24px 0 0 0;
            }

            /** PAGE */
            .page {
              font-family: 'Gilroy', sans-serif;
              font-size: 16px;
              font-weight: 400;
              line-height: 1.35;
              color: #333333;
            }
            .page .pageContainer {
              position: relative;
              margin: 0 auto;
            }
            .page .pageContent {
              margin: 0 12px;
            }
            .page .pageContent .pageContentHeader {
              display: flex;
              align-items: center;
              justify-cintent: center;
              background-color: #113376;
              color: white;
              margin: 0 -12px;
              padding-left: 12px;
            }
            .page .pageContent .pageContentHeader .pageContentHeaderLogo {
              position: relative;
              top: 2px;
            }
            .page .pageContent .pageContentHeader .pageContentHeaderLink {
              margin-left: auto;
              color: white;
              height: 48px;
              width: 48px;
              padding: 12px 0 0 12px;
              box-sizing: border-box;
            }
            .page .pageContent .pageContentHeader .pageContentHeaderMenu {
              cursor: pointer;
              border: none;
              background: none;
              outline: none;
              width: 48px;
              height: 48px;
              padding: 0;
              margin: 0;
              background-size: 36px 36px;
              background-position: center;
              background-repeat: no-repeat;
              background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='50' height='50'%3E%3Cline xmlns='http://www.w3.org/2000/svg' stroke-width='2' y2='30' x2='44' y1='30' x1='6' stroke='%23fff' fill='none'/%3E%3Cline xmlns='http://www.w3.org/2000/svg' stroke-width='2' y2='20' x2='44' y1='20' x1='6' stroke='%23fff' fill='none'/%3E%3C/svg%3E");
            }
            .page .pageContent .pageContentMain .pageContentTitle {
              margin: 12px 0 0 0;
              font-size: 28px;
            }
            .page .pageContent .pageContentMain .pageContentSubtitle {
              font-family: 'Gilroy', sans-serif;
              font-weight: 500;
              font-size: 16px;
              margin-top: 4px;
              color: #757575;
            }

            /** LIST */
            .page .pageContent .pageContentMain .pageContentList {
              padding: 0;
              margin: 12px 0 0;
              column-gap: 24px;
              list-style-type: none;
              margin-top: 18px;
            }
            .page .pageContent .pageContentMain .pageContentList .pageContentListItem {
              display: flex;
              align-items: baseline;
              justify-content: space-between;
              break-inside: avoid-column;
              margin-top: 8px;
            }
            .page
              .pageContent
              .pageContentMain
              .pageContentList
              .pageContentListItem
              .pageContentListItemText {
              color: #757575;
              flex: 1 1;
              display: flex;
              align-items: baseline;
              white-space: nowrap;
            }
            .page
              .pageContent
              .pageContentMain
              .pageContentList
              .pageContentListItem
              .pageContentListItemText::after {
              content: '';
              flex-grow: 1;
              margin: 0 8px;
              border-bottom: 1px solid #ececec;
            }
            .page .pageContent .pageContentMain .pageContentList .pageContentListItem span {
              font-weight: 500;
            }

            /** LINK BUTTON */
            .page .pageContent .pageContentMain .pageContentMainWebsite {
              display: block;
              height: 46px;
              width: 100%;
              background-color: #2969e0;
              color: white;
              border-radius: 8px;
              display: flex;
              align-items: center;
              justify-content: center;
              text-decoration: none;
              margin: 12px 0;
              font-weight: 500;
            }

            /** PRICE */
            .page .pageContent .pageContentMain .pageContentMainPrice {
              font-family: 'Gilroy', sans-serif;
              margin: 18px -12px;
              padding: 18px 12px;
              background-color: #5ece4c;
              font-weight: 600;
              font-size: 24px;
              text-align: center;
              color: #fff;
            }

            /** AMP CAROUSEL */
            amp-carousel {
              background-color: #f6f6f6;
              margin: 18px -12px 0 -12px;
            }
            amp-carousel .amp-carousel-button {
              background-color: white;
              border-radius: 50%;
              height: 46px;
              width: 46px;
              background-size: 22px 22px;
              box-shadow:
                0 0 1px rgba(0, 0, 0, 0.15),
                0 2px 6px rgba(52, 61, 68, 0.05),
                0 10px 20px rgba(52, 61, 68, 0.05);
              border: 1px solid #eee;
            }
            amp-carousel .amp-carousel-button-prev {
              background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23333333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cline x1='19' y1='12' x2='5' y2='12'/%3E%3Cpolyline points='12 19 5 12 12 5'/%3E%3C/svg%3E");
            }
            amp-carousel .amp-carousel-button-next {
              background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23333333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cline x1='5' y1='12' x2='19' y2='12'/%3E%3Cpolyline points='12 5 19 12 12 19'/%3E%3C/svg%3E");
            }

            /** AMP LIGHTBOX */
            amp-lightbox-gallery {
              z-index: 20;
            }

            /** AMP SIDEBAR */
            amp-sidebar {
              width: 100%;
              background-color: #ffffff;
            }
            amp-sidebar .pageSidebarItem {
              display: block;
              padding: 12px 18px;
              text-decoration: none;
              color: #2969e0;
              font-size: 16px;
            }
            amp-sidebar .pageSidebarItem:not(:last-child) {
              border-bottom: 1px solid #eee;
            }

            /** BANNER */
            .pageBanner {
              cursor: pointer;
              background-color: var(--color-neutral-100);
              padding: 18px 12px;
              box-sizing: border-box;
              margin: 0 -12px 12px;
            }
            .pageBanner a {
              color: inherit;
              text-decoration: none;
            }
            .pageBanner .pageBannerTitle {
              font-family: 'Montserrat', sans-serif;
              font-weight: 600;
              font-size: 23px;
            }
            .pageBanner .pageBannerSubtitle {
              margin-top: 8px;
            }
            .pageBanner .pageBannerSubtitle span {
              font-weight: 600;
              color: #2969e0;
              display: block;
            }

            /** MEDIA */
            @media (min-width: 801px) {
              .page .pageContainer {
                width: 460px;
              }

              amp-carousel {
                margin: 18px 0 0 0;
              }
            }
          `}
        </style>
        <div className="page">
          <div className="pageContainer">
            <div className="pageContent">
              <div className="pageContentHeader">
                <svg
                  viewBox="0 0 389 41"
                  width="100%"
                  height="100%"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M0 39.1495H12.8324L23.2736 11.1628H26.7009H31.9614V39.1495H45.272V0.877115L13.7091 0.797363L0 39.1495Z" />
                  <path d="M50.2933 20.0134C50.2933 25.9935 50.1339 32.3722 49.8948 39.1496H90.9425V29.5018H62.4084V27.349V26.4719V24.0001C64.0821 24.0001 66.6327 23.9204 69.9006 23.9204C72.1323 23.9204 74.4437 23.9204 76.7551 24.0001H87.1964V15.4685H76.7551C74.4437 15.4685 72.1323 15.5483 69.9006 15.5483C66.553 15.5483 64.0821 15.5483 62.4084 15.4685V12.5981V10.4453H90.9425V0.877197H49.8948C50.1339 7.65461 50.2933 14.0333 50.2933 20.0134Z" />
                  <path d="M127.846 39.1495H140.279C140.04 33.1695 139.881 26.7907 139.881 20.0133C139.881 13.1562 140.04 6.77743 140.279 0.797363H127.846V15.1495H108.398V0.797363H95.9639C96.2827 7.65451 96.3624 14.0332 96.3624 20.0133C96.3624 25.9934 96.203 32.3721 95.9639 39.1495H108.398V24.7974H127.846V39.1495Z" />
                  <path d="M189.377 39.1495H192.326V28.8638H189.377V0.877115L157.814 0.797363L147.692 28.8638H144.025V39.1495H156.938H176.067H189.377ZM167.299 11.1628H170.806H176.067V28.9435H160.763L167.299 11.1628Z" />
                  <path d="M228.193 39.4684H241.504V1.19593L209.941 1.11621L196.152 39.4684H209.064L219.426 11.4817H222.933H228.193V39.4684Z" />
                  <path d="M269.48 40.0266C277.61 40.0266 283.588 38.2724 287.413 34.7641C291.159 31.2558 293.072 26.3123 293.072 19.9336C293.072 13.6346 291.159 8.77076 287.413 5.26246C283.667 1.75415 277.689 0 269.56 0C261.43 0 255.532 1.75415 251.706 5.26246C247.96 8.77076 246.047 13.7143 246.047 19.9336C246.047 26.3123 247.96 31.1761 251.786 34.6844C255.452 38.2724 261.43 40.0266 269.48 40.0266ZM261.031 13.0764C262.785 11.5615 265.574 10.8439 269.4 10.8439C273.306 10.8439 276.095 11.5615 277.849 13.0764C279.602 14.5914 280.399 16.9036 280.399 20.0133C280.399 23.2026 279.523 25.5149 277.849 27.0299C276.095 28.5448 273.306 29.2624 269.4 29.2624C265.495 29.2624 262.705 28.5448 261.031 27.0299C259.278 25.5149 258.481 23.2027 258.481 20.093C258.481 16.9037 259.357 14.5914 261.031 13.0764Z" />
                  <path d="M335.873 4.94345C333.164 2.47169 328.222 1.19593 321.208 1.19593H310.209V1.11621H297.217C297.536 7.97335 297.615 14.3521 297.615 20.2524C297.615 26.2325 297.456 32.6112 297.217 39.4684H310.209V30.1395H321.208C326.07 30.1395 329.816 29.5016 332.526 28.3056C335.236 27.1096 337.228 25.4352 338.265 23.2026C339.38 21.0498 339.938 18.4185 339.938 15.3089C339.938 10.9235 338.583 7.41521 335.873 4.94345ZM327.186 18.4185C326.628 19.0564 325.592 19.4551 324.157 19.6943C322.722 19.9335 320.491 20.0132 317.621 20.0132H310.368V11.6411H317.621C320.65 11.6411 322.802 11.7209 324.316 11.9601C325.751 12.1993 326.787 12.5979 327.345 13.1561C327.903 13.7142 328.142 14.5913 328.142 15.7873C328.142 16.9833 327.744 17.7807 327.186 18.4185Z" />
                  <path d="M385.529 29.2624V1.19593L353.966 1.11621L343.844 29.1827H340.177V39.4684H353.09H372.219H385.609H388.558V29.1827H385.529V29.2624ZM372.219 29.2624H356.915L363.531 11.4817H367.038H372.298V29.2624H372.219Z" />
                </svg>
                <a className="pageContentHeaderLink" href="https://landlord.ru">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24px"
                    height="24px"
                    viewBox="0 0 256 256"
                    version="1.1"
                    preserveAspectRatio="xMidYMid"
                  >
                    <path
                      d="M127.962816,0 C198.911815,0.558586285 255.754376,55.4725719 255.999327,129.30333 C256.222426,196.981097 200.939466,257.457031 124.744539,255.972592 C55.244106,254.617978 -1.52329281,198.006026 0.0311827848,124.08815 C1.40287938,58.8018828 54.6684376,0.674744901 127.962816,0 Z M101.003768,147.357453 C105.554452,147.357453 110.106845,147.367703 114.65753,147.352329 C116.833796,147.345496 118.196951,147.837462 117.689611,150.565481 C116.574147,156.564732 115.660252,162.599855 114.679737,168.621313 C112.832587,179.971832 110.991701,191.32349 109.157078,202.676286 C108.981132,203.757586 108.415712,204.914048 109.809616,205.660538 C112.182326,206.929741 118.670127,205.31377 120.011075,203.0743 C137.018063,174.694017 154.01651,146.30861 171.006416,117.918077 C174.535588,112.012778 172.683883,108.820124 165.760488,108.768878 C157.794512,108.711938 149.828537,108.711938 141.862561,108.768878 C139.389066,108.782544 137.708182,108.456275 138.304349,105.219207 C140.080893,95.5899998 141.703697,85.9334607 143.331626,76.2769217 C144.604815,68.7175012 145.824481,61.1489702 146.990622,53.5713287 C147.16486,52.4404904 147.853271,51.0414624 146.011815,50.3701339 C142.079426,49.2279869 137.854573,50.6944931 135.475545,54.0274221 C122.392896,75.8151342 109.320497,97.6091098 96.2583467,119.409349 C92.3157866,125.985976 88.2553597,132.494275 84.4989951,139.176812 C81.6838569,144.181882 83.5321454,147.282292 89.0565125,147.34208 C93.0383616,147.384785 97.0219188,147.350621 101.005476,147.359162 L101.003768,147.357453 Z"
                      fill="#ffffff"
                    />
                  </svg>
                </a>
                <amp-sidebar id="sidebar1" layout="nodisplay" side="right">
                  <a
                    className="pageSidebarItem"
                    href="https://landlord.ru/objects"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Вторичная
                  </a>
                  <a
                    className="pageSidebarItem"
                    href="https://landlord.ru/mortgage"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Ипотека
                  </a>
                  <a
                    className="pageSidebarItem"
                    href="https://landlord.ru/services"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Услуги
                  </a>
                  <a
                    className="pageSidebarItem"
                    href="https://landlord.ru/contacts"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Контакты
                  </a>
                  <a
                    className="pageSidebarItem"
                    href="https://landlord.ru/about"
                    target="_blank"
                    rel="noreferrer"
                  >
                    О нас
                  </a>
                </amp-sidebar>
                <button className="pageContentHeaderMenu" type="button" on="tap:sidebar1.toggle" />
              </div>
              <div className="pageContentMain">
                {!data.isActual && (
                  <div className="pageBanner">
                    <a href={similarLinkGenerator()}>
                      <div className="pageBannerTitle">Объект снят с публикации</div>
                      <div className="pageBannerSubtitle">
                        Сожалеем, но этот объект уже недоступен.{' '}
                        <span>Посмотреть другие варианты в этой категории?</span>
                      </div>
                    </a>
                  </div>
                )}
                <h1 className="pageContentTitle">{getAMPTitle()}</h1>
                <h2 className="pageContentSubtitle">{getAddress(true, true, true, false)}</h2>
                {Boolean(data.isActual) && (
                  <amp-carousel
                    width="450"
                    height="300"
                    layout="responsive"
                    type="slides"
                    aria-label="Галерея"
                    data-prev-button-aria-label="Предыдущее фото"
                    data-next-button-aria-label="Следущее фото"
                    data-button-count-format="(%s из %s)"
                    lightbox
                  >
                    {data.images.map((v, i) => (
                      <amp-img
                        src={v.value}
                        layout="responsive"
                        width="450"
                        height="300"
                        lightbox
                        key={i}
                      />
                    ))}
                  </amp-carousel>
                )}
                <h3>Описание</h3>
                {data.description
                  .replace(/\n\n/g, '\n')
                  .split('\n')
                  .map((value, index) => (
                    <p key={index}>{value}</p>
                  ))}
                <h3>{data.category.charAt(0).toUpperCase() + data.category.slice(1)}</h3>
                <ul className="pageContentList">
                  {Object.entries(data.object).map((value, index) => {
                    if (value[0] === 'studio')
                      return value[1] ? (
                        <li className="pageContentListItem" key={index}>
                          <div className="pageContentListItemText">Комнат</div>
                          <span>студия</span>
                        </li>
                      ) : (
                        false
                      );

                    if (value[0] === 'area') {
                      return Object.entries(value[1]).map((v, i) => (
                        <li className="pageContentListItem" key={i}>
                          <div className="pageContentListItemText">{language[v[0]]}</div>
                          <span>
                            {typeOf(v[1]) === 'object'
                              ? `${v[1].value} ${v[1].unit}`
                              : typeOf(v[1]) === 'array'
                                ? v[1].join(', ')
                                : v[1]}
                          </span>
                        </li>
                      ));
                    }

                    return (
                      <li className="pageContentListItem" key={index}>
                        <div className="pageContentListItemText">{language[value[0]]}</div>
                        <span>
                          {typeOf(value[1]) === 'object'
                            ? `${value[1].value} ${value[1].unit}`
                            : typeOf(value[1]) === 'array'
                              ? value[1].join(', ')
                              : value[1]}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                {Boolean(data.isActual) && (
                  <div className="pageContentMainPrice">
                    {toMoney(data.price.value)} {data.price.currency}
                  </div>
                )}
                <a className="pageContentMainWebsite" href="https://landlord.ru">
                  Перейти на оригинал
                </a>
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <NextSeo
        title={htmlTitleGenerator()}
        description={htmlDescriptionGenerator()}
        openGraph={{
          url: `https://landlord.ru/object/${id}`,
          type: 'website',
          title: htmlTitleGenerator(),
          description: data.description.replace(/\n|\r|"/g, ' '),
          images: [
            {
              url: data.images[0].value
            }
          ],
          site_name: 'LANDLORD'
        }}
      />
      <BreadcrumbJsonLd itemListElements={breadcrumbsGenerator()} />
      <Header />
      <main>
        <section className="block">
          {!data.isActual && (
            <div className={['fixed', styles.banner].join(' ')}>
              <a href={similarLinkGenerator()}>
                <div className={styles.bannerTitle}>Объект снят с публикации</div>
                <div className={styles.bannerSubtitle}>
                  Сожалеем, но этот объект уже недоступен.{' '}
                  <span>Хотите посмотреть похожие объекты?</span>
                </div>
              </a>
            </div>
          )}
          <div className="fixed columns">
            <ObjectInformationContent data={data} />
            {Boolean(data.isActual) && (
              <Fragment>
                <ObjectInformationOwner data={data} />
                <ObjectContactsMobile data={data} />
              </Fragment>
            )}
          </div>
        </section>
        <LazyLoad>
          <ObjectSimilar id={id} object={data} />
        </LazyLoad>
      </main>
      <Footer />
    </Fragment>
  );
};

export async function getServerSideProps({ query }) {
  const response = await axios.get(process.env.NEXT_PUBLIC_API + `/object/${query.id}`);

  if (!response.data || response.data.status !== 'ok') {
    return {
      notFound: true
    };
  }

  return {
    props: {
      id: query.id,
      data: response.data.data
    }
  };
}

export default ObjectPage;
